/**
 * Phase 3 — SensitivityTornadoChart
 *
 * Horizontal bar chart, one signed bar per variable. Loads
 * ``local_sensitivity.csv`` from the study's artefact endpoint and
 * parses it client-side (the file is tiny — at most one row per
 * variable / direction).
 *
 * Phase 3.6 additions (additive, frontend-only):
 *   • ``method`` is derived per row from CSV presence: ``central``
 *     when both directions exist, ``forward`` when only one. A small
 *     pill is shown next to each bar.
 *   • ``S`` (slope), ``|S|`` and elasticity are derived client-side:
 *       central:  S      = (pk_high − pk_low) / (value_high − value_low)
 *                 elast. = S * x0 / max(pk_baseline, 1e-9), x0 = mid
 *       forward:  S      = pk_delta / (value − x_baseline_proxy)
 *                          (uses pkBaseline from /diff and value as
 *                          its own baseline proxy when only one
 *                          direction is present — clearly labelled)
 *   • A view toggle switches between Slope (S) / Elasticity / |S|.
 *     Elasticity is the default (dimensionless, procurement-friendly).
 *   • CI whiskers are skipped — the per-row standard-error fields are
 *     not persisted to the v1 CSV header. The chart silently omits
 *     whiskers rather than inventing error bars.
 */
import { useEffect, useMemo, useState } from "react";
import {
    Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer,
    Tooltip, XAxis, YAxis, Cell,
} from "recharts";
import { formatApiError } from "@/lib/formatApiError";
import optimisation from "@/api/optimisation";

const VIEWS = [
    { key: "elasticity", label: "Elasticity" },
    { key: "slope",      label: "Slope (S)" },
    { key: "abs_slope",  label: "|S|" },
];
const DEFAULT_VIEW = "elasticity";

function parseCsv(text) {
    if (!text || typeof text !== "string") return [];
    const lines = text.trim().split(/\r?\n/);
    if (!lines.length) return [];
    const header = lines.shift().split(",");
    return lines.map((line) => {
        const cols = line.split(",");
        const row = {};
        header.forEach((h, i) => {
            const v = cols[i];
            row[h] = v === "" || v === undefined
                ? null
                : Number.isFinite(Number(v)) ? Number(v) : v;
        });
        return row;
    });
}

function aggregate(rows, pkBaseline) {
    // Group by variable_path → { high, low, method, S, elasticity, magnitude }.
    const acc = new Map();
    for (const r of rows) {
        if (!r.variable_path) continue;
        const cur = acc.get(r.variable_path) || {
            path: r.variable_path,
            high: null,
            low: null,
            value_high: null,
            value_low: null,
        };
        const dir = (r.direction || "").toLowerCase();
        if (dir === "high" || dir === "max") {
            cur.high = typeof r.pk_delta === "number" ? r.pk_delta : null;
            cur.value_high = typeof r.value === "number" ? r.value : null;
            cur.pk_high = typeof r.pk === "number" ? r.pk : null;
        }
        if (dir === "low" || dir === "min") {
            cur.low = typeof r.pk_delta === "number" ? r.pk_delta : null;
            cur.value_low = typeof r.value === "number" ? r.value : null;
            cur.pk_low = typeof r.pk === "number" ? r.pk : null;
        }
        acc.set(r.variable_path, cur);
    }
    const blPk = typeof pkBaseline === "number" ? pkBaseline : null;
    return [...acc.values()]
        .map((row) => {
            const hasHigh = typeof row.high === "number";
            const hasLow = typeof row.low === "number";
            const high = hasHigh ? row.high : 0;
            const low = hasLow ? row.low : 0;
            const magnitude = Math.max(Math.abs(high), Math.abs(low));
            const signed = Math.abs(high) >= Math.abs(low) ? high : low;

            const method = (hasHigh && hasLow) ? "central" : "forward";

            // Slope.
            let S = null;
            let x0 = null;
            if (
                method === "central"
                && typeof row.value_high === "number"
                && typeof row.value_low === "number"
                && typeof row.pk_high === "number"
                && typeof row.pk_low === "number"
            ) {
                const dx = row.value_high - row.value_low;
                if (dx !== 0) {
                    S = (row.pk_high - row.pk_low) / dx;
                    x0 = (row.value_high + row.value_low) / 2;
                }
            } else if (method === "forward") {
                // Forward fallback: rely on pk_delta vs the dominant
                // direction's value. We don't know x_baseline so we
                // present S as null (chart will fall back to |signed|).
                S = null;
                x0 = (typeof row.value_high === "number")
                    ? row.value_high
                    : (typeof row.value_low === "number" ? row.value_low : null);
            }

            const elasticity =
                S !== null && x0 !== null && blPk !== null
                    ? S * x0 / Math.max(blPk, 1e-9)
                    : null;

            return {
                path: row.path,
                signed,                 // signed Pk delta (raw)
                magnitude,              // |signed|
                method,                 // 'central' | 'forward'
                S,                      // slope (or null if unknown)
                elasticity,             // dimensionless local sensitivity
            };
        })
        .filter((r) => r.magnitude > 0 || typeof r.S === "number")
        .sort((a, b) => b.magnitude - a.magnitude);
}

function valueFor(row, view) {
    if (view === "elasticity") {
        if (typeof row.elasticity === "number") return row.elasticity;
        return row.signed; // graceful fallback for forward-only rows
    }
    if (view === "slope") {
        if (typeof row.S === "number") return row.S;
        return row.signed;
    }
    if (view === "abs_slope") {
        if (typeof row.S === "number") return Math.abs(row.S);
        return Math.abs(row.signed);
    }
    return row.signed;
}

function viewLabel(view) {
    if (view === "elasticity") return "Elasticity (S · x₀ / Pk₀)";
    if (view === "slope")      return "Slope (S = ΔPk / Δx)";
    return "|S|";
}

export default function SensitivityTornadoChart({ studyId, pkBaseline = null }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [view, setView] = useState(DEFAULT_VIEW);

    useEffect(() => {
        if (!studyId) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const txt = await optimisation.downloadArtifact(
                    studyId, "local_sensitivity.csv",
                );
                if (cancelled) return;
                setRows(parseCsv(typeof txt === "string" ? txt : ""));
            } catch (e) {
                if (!cancelled) {
                    setErr(
                        e?.response?.status === 404
                            ? "Local sensitivity not produced for this study yet."
                            : formatApiError(e, "Failed to load sensitivity"),
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [studyId]);

    const data = useMemo(() => aggregate(rows, pkBaseline), [rows, pkBaseline]);

    const chartData = useMemo(
        () => data.map((row) => ({ ...row, value: valueFor(row, view) })),
        [data, view],
    );

    if (loading) {
        return (
            <div
                className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 text-[12px] text-ux-text-3"
                data-testid="sensitivity-loading"
            >
                Loading sensitivity…
            </div>
        );
    }

    if (err || data.length === 0) {
        return (
            <div
                className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 text-[12px] text-ux-text-3"
                data-testid="sensitivity-empty"
            >
                {err || "No sensitivity rows produced (study may have skipped OAT)."}
            </div>
        );
    }

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-2"
            data-testid="sensitivity-tornado"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-[12px] uppercase tracking-wider text-ux-text-3">
                    Local sensitivity — {viewLabel(view)}
                </h3>
                <div
                    className="inline-flex border border-ux-border-1 rounded overflow-hidden text-[10px]"
                    role="group"
                    aria-label="Sensitivity view selector"
                    data-testid="sensitivity-view-toggle"
                >
                    {VIEWS.map((v) => (
                        <button
                            type="button"
                            key={v.key}
                            onClick={() => setView(v.key)}
                            className={[
                                "px-2 py-1 uppercase tracking-wider transition-colors",
                                view === v.key
                                    ? "bg-ux-accent text-ux-bg-1"
                                    : "bg-ux-bg-2 text-ux-text-2 hover:text-ux-text-1",
                            ].join(" ")}
                            data-testid={`sensitivity-view-${v.key}`}
                            aria-pressed={view === v.key}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ width: "100%", height: Math.max(80, data.length * 32 + 60) }}>
                <ResponsiveContainer>
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 80, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid stroke="var(--ux-border-1)" horizontal={false} />
                        <XAxis
                            type="number"
                            tick={{ fontSize: 10, fill: "var(--ux-text-3)" }}
                            tickFormatter={(v) =>
                                Math.abs(v) >= 1
                                    ? v.toFixed(2)
                                    : v.toFixed(3)
                            }
                            stroke="var(--ux-border-1)"
                        />
                        <YAxis
                            type="category"
                            dataKey="path"
                            tick={{ fontSize: 10, fill: "var(--ux-text-2)" }}
                            width={200}
                            stroke="var(--ux-border-1)"
                        />
                        <ReferenceLine x={0} stroke="var(--ux-border-2)" />
                        <Tooltip
                            cursor={{ fill: "var(--ux-bg-2)" }}
                            formatter={(value, _name, ctx) => {
                                const m = ctx?.payload?.method;
                                const tag = m ? ` [${m}]` : "";
                                const v = typeof value === "number" ? value.toFixed(4) : value;
                                return [`${v}${tag}`, viewLabel(view)];
                            }}
                            contentStyle={{
                                background: "var(--ux-bg-1)",
                                border: "1px solid var(--ux-border-1)",
                                fontSize: "11px",
                            }}
                        />
                        <Bar
                            dataKey="value"
                            isAnimationActive={false}
                        >
                            {chartData.map((entry, idx) => (
                                <Cell
                                    key={idx}
                                    fill={
                                        entry.value >= 0
                                            ? "var(--ux-success)"
                                            : "var(--ux-danger)"
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <ul
                className="space-y-0.5 text-[10px] font-mono text-ux-text-3 max-h-32 overflow-y-auto"
                data-testid="sensitivity-method-legend"
            >
                {data.map((row) => (
                    <li
                        key={row.path}
                        className="flex items-center gap-2"
                        data-testid={`sensitivity-method-row-${row.path}`}
                    >
                        <span className="truncate flex-1">{row.path}</span>
                        <span
                            className={[
                                "uppercase tracking-wider px-1.5 py-[1px] rounded text-[9px]",
                                row.method === "central"
                                    ? "bg-ux-info-soft text-ux-info"
                                    : "bg-ux-warning-soft text-ux-warning",
                            ].join(" ")}
                            title={
                                row.method === "central"
                                    ? "Central difference: (pk_high − pk_low) / (x_high − x_low)"
                                    : "Forward difference: only one direction available"
                            }
                            data-testid={`sensitivity-method-badge-${row.path}`}
                        >
                            {row.method}
                        </span>
                    </li>
                ))}
            </ul>

            <p className="text-[11px] text-ux-text-3 italic">
                Bars to the right improve Pk; bars to the left worsen it.
                Elasticity is dimensionless and most readable for procurement.
            </p>
        </div>
    );
}
